import { FunctionComponent, useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  rucksack,
  Typography,
} from '@black-bear-energy/rucksack'
import { CheckRounded, CloseRounded } from '@mui/icons-material'
import { CenteredModal } from '../CenteredModal'
import { BoldText } from './BoldText'
import { FileUploadInput } from './FileUploadInput'
import { useUploadChecklistDocument } from '../../api/hooks/useUploadChecklistDocument'
import { getUploadDisplayName } from '../../utils/getUploadDisplayName'

export const UploadDocumentModal: FunctionComponent<
  UploadDocumentModalProps
> = ({
  open,
  documentId,
  documentName,
  onClose,
  hasExistingUpload,
  isDocumentCollection = false,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const { mutate: uploadChecklistDocument } = useUploadChecklistDocument()

  /** When the user clicks 'Save', upload the file(s) and close the modal. */
  const handleSave = () => {
    uploadChecklistDocument({
      documentId,
      documentName,
      files: selectedFiles,
    })
    handleClose()
  }

  const handleClose = () => {
    // clear out the selected files, in case the user opens this modal again
    setSelectedFiles([])
    onClose()
  }

  const filesSelected = selectedFiles.length > 0

  return (
    <CenteredModal open={open}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          alignSelf: 'stretch',
        }}
      >
        <Typography variant="h6">Upload Document</Typography>
        <IconButton onClick={handleClose}>
          <CloseRounded />
        </IconButton>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          gap: '8px',
          alignSelf: 'stretch',
        }}
      >
        <Typography variant="body1">
          Upload file(s) for the <BoldText>{documentName}</BoldText>. To upload
          multiple files, select all files to upload together.
        </Typography>
        {hasExistingUpload && (
          <Typography
            variant="body1"
            sx={{ color: rucksack.palette.error.main }}
          >
            <BoldText>Warning:</BoldText> Replaces existing upload.
          </Typography>
        )}
      </Box>

      <FileUploadInput onSelectedFileChange={setSelectedFiles} />
      {isDocumentCollection && selectedFiles.length > 1 && (
        <Typography variant="body1" sx={{ alignSelf: 'stretch' }}>
          This upload will be grouped and renamed{' '}
          <BoldText>“{getUploadDisplayName(selectedFiles)}”</BoldText>.
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '14px',
          alignSelf: 'stretch',
        }}
      >
        <Button
          sx={{
            color: rucksack.palette.primary.dark,
            border: '1px solid rgba(73, 173, 214, 0.50)',
            padding: '6px 16px',
          }}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!filesSelected}
          startIcon={<CheckRounded />}
          sx={{
            background: filesSelected
              ? rucksack.palette.primary.light
              : rucksack.palette.action.disabledBackground,
            color: filesSelected
              ? rucksack.palette.primary.contrastText
              : rucksack.palette.action.disabled,
            padding: '6px 16px',
          }}
        >
          Save
        </Button>
      </Box>
    </CenteredModal>
  )
}

export interface UploadDocumentModalProps {
  open: boolean
  documentId: number
  documentName: string
  onClose: () => void
  hasExistingUpload: boolean
  /** Whether this modal uploads into a document collection, where multiple files are grouped and renamed. */
  isDocumentCollection?: boolean
}
